import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import { useCustomerProfileMutation } from './hooks/useCustomerQuery'

/**
 * Port of `app/customer/editCustomerProfile.html` +
 * `app/customer/editCustomerProfileController.js` (AngularJS
 * `editCustomerProfileController`) — the "Atualizar Permissão de limpeza de
 * conta" screen, reached at `/workspace/edit-customer-profile/:profile`
 * (legacy state `workspace.customerProfile`, URL
 * `/workspace/editCustomerProfile/{profile}`; see `AppRoutes.jsx`), the only
 * entry point being `CustomersListPage`'s "Habilitar limpeza de conta" row
 * action (`editCleanAccountPermisison`, task 025).
 *
 * Despite the task brief's generic "formulário de edição completo" wording,
 * reading `editCustomerProfileController.js`/`editCustomerProfile.html` in
 * full shows the legacy screen is narrowly scoped to a single boolean
 * setting — there is no multi-field customer-profile edit form anywhere in
 * this controller/template pair (confirmed: the controller only defines
 * `initView()` and `editProfile(profile)`, and the template's only editable
 * input is the `isCleanAccountAllowed` checkbox/switch). Ported 1:1, field
 * by field:
 * - `$stateParams.profile` (a JSON string built by `CustomersListPage`'s
 *   `editCleanAccountPermisison`: `{tenantId, namespace,
 *   isCleanAccountAllowed}`) -> React Router's `useParams().profile`,
 *   `JSON.parse`d the same way `initView()` does. React Router already
 *   URL-decodes route params, so no extra `decodeURIComponent` call is
 *   needed on top of `JSON.parse` (same pattern as `MigrationJsonPage`
 *   consuming `MigrationDetailPage`'s `entity` param).
 * - Table with one row: a switch bound to `tenantProfile.isCleanAccountAllowed`
 *   (legacy Materialize "OFF/ON" toggle -> MUI `Switch`, wrapped in a
 *   `FormControlLabel` for the "OFF"/"ON" side labels) plus two read-only
 *   cells for `namespace`/`tenantId`.
 * - "Cancelar" (`ui-sref="workspace.customers"`) -> navigates to
 *   `/workspace/customers`.
 * - "Salvar" (`ng-click="editProfile(tenantProfile)"`) ->
 *   `useCustomerProfileMutation().mutate(tenantProfile)`
 *   (`CustomerService.setCleanAccountPermission`, see
 *   `./hooks/useCustomerQuery.js`). On success: legacy shows a Materialize
 *   toast ("Permissão alterada com sucesso!") then navigates to
 *   `workspace.customers` -> ported as a MUI `Snackbar` immediately followed
 *   by `navigate('/workspace/customers')` (legacy doesn't wait for the toast
 *   to be seen either, it fires both essentially together). On error: legacy
 *   shows an error toast and stays on the page -> ported the same way, no
 *   navigation.
 * - `$scope.loading`, toggled around the request exactly like every other
 *   ported list/detail screen's `isPending` -> disables "Salvar" while the
 *   mutation is in flight (the legacy template doesn't visibly use
 *   `loading` for anything — no spinner/disabled state actually reads it in
 *   `editCustomerProfile.html`, confirmed via `grep -n "loading"
 *   app/customer/editCustomerProfile.html`, no hits — but disabling the
 *   button during the request is still a direct, uncontroversial mapping of
 *   `$scope.loading`'s intent, not new behavior).
 *
 * Not ported: the legacy template's `$('select').material_select()`
 * `$(document).ready` snippet — there is no `<select>` element anywhere on
 * this screen (confirmed by reading the full template), so it's dead
 * jQuery/Materialize initialization with nothing to attach to, same
 * category of dead presentational code already documented as "not ported"
 * in `CustomersListPage.jsx`/`plan.md`'s UI-kit decision.
 *
 * NOTE (malformed/missing `profile` param): the legacy `initView()` does a
 * bare `JSON.parse($stateParams.profile)` with no try/catch — if `profile`
 * were ever missing or invalid JSON, AngularJS would throw inside
 * `ng-init`, leaving the screen half-rendered with whatever `tenantProfile`
 * happened to be (`{}`, from the controller's own initial assignment).
 * Reproduced with the same effective behavior: parsing is wrapped in a
 * `try/catch` here only because unlike AngularJS's silent-ish digest-cycle
 * error, an uncaught throw during React's render would blank the entire
 * page (an unhandled error boundary crash) instead of leaving a mostly-blank
 * table — falling back to `{}` keeps the same "blank fields, page still
 * usable" end state as the legacy screen without introducing a harder
 * crash. This is a resilience adaptation to React's stricter render
 * semantics, not a behavior change to the happy path.
 */
function parseProfile(profileParam) {
  try {
    return JSON.parse(profileParam)
  } catch {
    return {}
  }
}

function EditCustomerProfilePage() {
  const { profile } = useParams()
  const navigate = useNavigate()
  const profileMutation = useCustomerProfileMutation()

  const [tenantProfile, setTenantProfile] = useState(() => parseProfile(profile))
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' })

  function notify(message, severity = 'info') {
    setSnackbar({ open: true, message, severity })
  }

  function handleSnackbarClose() {
    setSnackbar((current) => ({ ...current, open: false }))
  }

  function handleToggleCleanAccountAllowed(event) {
    setTenantProfile((current) => ({ ...current, isCleanAccountAllowed: event.target.checked }))
  }

  function handleCancel() {
    navigate('/workspace/customers')
  }

  /** Port of `editProfile(profile)`. */
  function handleSave() {
    profileMutation.mutate(tenantProfile, {
      onSuccess: () => {
        notify('Permissão alterada com sucesso!')
        navigate('/workspace/customers')
      },
      onError: () => notify('Ocorreu um erro a permissão de limpeza de conta!', 'error'),
    })
  }

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Atualizar Permissão de limpeza de conta
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Permissão</TableCell>
              <TableCell>namespace</TableCell>
              <TableCell>tenantId</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(tenantProfile.isCleanAccountAllowed)}
                      onChange={handleToggleCleanAccountAllowed}
                      slotProps={{ input: { 'aria-label': 'Permissão de limpeza de conta' } }}
                    />
                  }
                  label={tenantProfile.isCleanAccountAllowed ? 'ON' : 'OFF'}
                />
              </TableCell>
              <TableCell>{tenantProfile.namespace}</TableCell>
              <TableCell>{tenantProfile.tenantId}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end' }}>
        <Button onClick={handleCancel}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={profileMutation.isPending}>
          Salvar
        </Button>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default EditCustomerProfilePage
